import React, { useState } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormInput,
  CFormLabel,
  CButton,
  CAlert,
  CSpinner,
  CInputGroup,
  CFormSelect // NEW IMPORT
} from '@coreui/react';
import { resellerAPI } from '../../services/api';

const CreateResellerUserModal = ({ show, onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user', // Default role
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Role options for dropdown
  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'manager', label: 'Manager' },
    { value: 'customer_support', label: 'Customer Support' },
    { value: 'other', label: 'Other' }
  ];

  // Password validation rules
  const passwordRules = [
    { test: (pwd) => pwd.length >= 8, message: 'At least 8 characters' },
    { test: (pwd) => /[A-Z]/.test(pwd), message: 'One uppercase letter (A-Z)' },
    { test: (pwd) => /[a-z]/.test(pwd), message: 'One lowercase letter (a-z)' },
    { test: (pwd) => /[0-9]/.test(pwd), message: 'One digit (0-9)' },
    { test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd), message: 'One special character (!@#$%^&* etc.)' }
  ];

  // Validate password function
  const validatePassword = (password) => {
    const errors = passwordRules
      .filter(rule => !rule.test(password))
      .map(rule => rule.message);
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    if (error) setError('');
    if (success) setSuccess('');

    if (name === 'password') {
      validatePassword(value);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!validatePassword(formData.password)) {
      setError('Please fix password validation errors');
      setLoading(false);
      return;
    }

    try {
      const response = await resellerAPI.addResellerUser(formData);
      if (response.data.success) {
        setSuccess('User created successfully!');
        setFormData({
          email: '',
          password: '',
          name: '',
          role: 'user', // Reset to default
          is_active: true
        });
        setPasswordErrors([]);
        if (onUserAdded) {
          onUserAdded();
        }
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'user', // Reset to default
      is_active: true
    });
    setError('');
    setSuccess('');
    setPasswordErrors([]);
    setShowPassword(false);
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <div>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040,
        }}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1050,
          width: '90%',
          maxWidth: '600px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        }}
      >
        <div 
          style={{ 
            borderBottom: '1px solid #dee2e6',
            padding: '1rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h5 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
            Add Reseller User
          </h5>
          <button 
            onClick={handleClose}
            style={{ 
              border: 'none',
              background: 'transparent',
              fontSize: '1.5rem',
              lineHeight: 1,
              padding: '0.5rem',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{ padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}>
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-danger mb-3">
                {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success mb-3">
                {success}
              </div>
            )}
            
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Password</label>
                <div className="input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-control"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    style={{
                      borderColor: formData.password && passwordErrors.length > 0 ? '#e55353' : '#d8dbe0'
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={togglePasswordVisibility}
                    disabled={loading}
                    style={{ minWidth: '80px' }}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                
                {/* Password Validation Rules */}
                {formData.password && (
                  <div className="mt-2">
                    <small className="text-muted">Password must contain:</small>
                    <div className="mt-1">
                      {passwordRules.map((rule, index) => {
                        const isValid = rule.test(formData.password);
                        return (
                          <div key={index} className="d-flex align-items-center mb-1">
                            <div 
                              className={`me-2 ${isValid ? 'text-success' : 'text-danger'}`}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {isValid ? '✓' : '✗'}
                            </div>
                            <small className={isValid ? 'text-success' : 'text-muted'}>
                              {rule.message}
                            </small>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="col-md-6 mb-3">
                <label className="form-label">Role</label>
                <select
                  name="role"
                  className="form-select"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_active"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="is_active">
                  Active User
                </label>
              </div>
            </div>
          </form>
        </div>
        
        <div style={{ borderTop: '1px solid #dee2e6', padding: '1rem 1.5rem' }}>
          <button 
            className="btn btn-secondary me-2"
            onClick={handleClose} 
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || (formData.password && passwordErrors.length > 0)}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Creating...
              </>
            ) : (
              'Create User'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateResellerUserModal;